// The in-pane read-only preview of a past version (F-VERSIONS-PREVIEW).
// Renders the loading / error / sections states of one fetched écriture detail
// + a close affordance back to the history list. Pure presentation (W1), no
// Office.js (W2) — the section bodies render via `SectionRenderer`.
//
// §9 — logical RTL utilities; the BE écriture title + each section title get
//      per-string direction via `bidiProps` (content can be Arabic in an FR
//      chrome). Error region carries `role="alert"`.

import { useTranslation } from 'react-i18next'
import { bidiProps } from '@/lib/bidi'
import { SectionRenderer } from '@/ui/components/SectionRenderer'
import type { EcritureDetail } from '@/domain/ecriture/entity'

interface VersionPreviewProps {
  detail: EcritureDetail | null
  loading: boolean
  error: Error | null
  /** Close the preview (back to the version history list). */
  onClose: () => void
}

export function VersionPreview({
  detail,
  loading,
  error,
  onClose,
}: VersionPreviewProps): JSX.Element {
  const { t } = useTranslation()

  return (
    <div
      data-testid="version-preview"
      className="flex flex-1 flex-col overflow-y-auto"
    >
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-ink/10 bg-white px-3.5 py-2">
        <span className="truncate text-[11px] font-semibold text-ink">
          {detail ? (
            <span {...bidiProps(detail.title)}>{detail.title}</span>
          ) : (
            t('versions.preview.title')
          )}
        </span>
        <button
          type="button"
          data-testid="version-preview-close"
          onClick={onClose}
          className="shrink-0 rounded px-2 py-1 text-[10px] font-medium text-ink/70 hover:bg-ink/5"
        >
          {t('versions.preview.close')}
        </button>
      </div>

      {loading && (
        <div
          data-testid="version-preview-loading"
          className="px-3.5 py-3 text-xs text-ink/60"
        >
          {t('versions.preview.loading')}
        </div>
      )}

      {error && (
        <div
          role="alert"
          data-testid="version-preview-error"
          className="m-2 rounded-md border-s-2 border-warn bg-warn/5 px-2.5 py-1.5 text-[11px] text-ink/80"
        >
          {t('versions.preview.error')}
        </div>
      )}

      {!loading && !error && detail && (
        <div className="flex flex-col gap-4 px-3.5 py-3">
          {detail.sections.length === 0 ? (
            <p
              data-testid="version-preview-empty"
              className="text-xs text-ink/60"
            >
              {t('versions.preview.emptySection')}
            </p>
          ) : (
            detail.sections.map((section) => (
              <section
                key={section.id}
                data-testid={`version-preview-section-${section.id}`}
                className="flex flex-col gap-1.5"
              >
                <h4
                  className="text-xs font-bold text-ink"
                  {...bidiProps(section.title)}
                >
                  {section.title}
                </h4>
                {section.contentJson ? (
                  <SectionRenderer
                    doc={section.contentJson}
                    registry={detail.registry}
                  />
                ) : (
                  <p className="text-xs italic text-ink/50">
                    {t('versions.preview.emptySection')}
                  </p>
                )}
              </section>
            ))
          )}
        </div>
      )}
    </div>
  )
}
